import axios from 'axios';
import { Cloudinary } from 'cloudinary-core';
import { getCloudinarySignature } from './api';

const uploadPreset = process.env.REACT_APP_CLOUDINARY_PRESET;
const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD;
const uploadEndpoint = process.env.REACT_APP_CLOUDINARY_UPLOAD_ENDPOINT.replace(
  '<cloudName>',
  cloudName
);
const apiKey = process.env.REACT_APP_CLOUDINARY_API_KEY;

const cloudinaryInstance = new Cloudinary({
  cloud_name: cloudName,
  secure: true,
});


export const UploadImage = async ({ file, id, folder = '', onProgress }) => {
  try {
    // First, get the signature and timestamp from your server
    const signatureResponse = await getCloudinarySignature({ public_id: id, transformation: 'f_webp', folder })

    const { signature, timestamp } = signatureResponse;

    // Prepare the FormData for the image upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('public_id', id); // optional, can let Cloudinary generate it
    formData.append('api_key', apiKey);
    formData.append('timestamp', timestamp);
    formData.append('signature', signature);
    formData.append('transformation', 'f_webp');
    if (folder) {
      formData.append('folder', folder);
    }

    // Upload to Cloudinary
    const response = await axios.post(
      uploadEndpoint,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: evt => {
          if (!evt?.total) return;
          const progress = Math.round((evt.loaded / evt.total) * 100);
          onProgress?.(progress);
        },
      }
    );

    // Handle the successful upload response
    console.log('Image uploaded:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error uploading image:', error);
    // Re-throw the error instead of silently returning undefined
    throw new Error(`Cloudinary upload failed: ${error.message || 'Unknown error'}`);
  }
};


export const cloudinaryModernOptions = {
  format: 'auto',
  quality: 'auto:eco',  // Aggressive compression
  dpr: 'auto'           // Adjust resolution based on user's device
};

export function getUrlVariants(secureUrl, variant, options = cloudinaryModernOptions) {

  // Define the variants
  const variants = {
    'listing-card-6x': { transformation: 'w_2400,h_2400', height: 2400, width: 2400 },
    'listing-card-4x': { transformation: 'w_1600,h_1600', height: 1600, width: 1600 },
    'listing-card-2x': { transformation: 'w_800,h_800,c_fit', height: 800, width: 800 },
    'listing-card': { transformation: 'w_400,h_400,c_fit', height: 400, width: 400 },
    'landscape-crop': { transformation: 'c_crop,w_400,h_267', height: 267, width: 400 },
    'landscape-crop2x': { transformation: 'c_crop,w_800,h_533', height: 533, width: 800 },
    'landscape-crop4x': { transformation: 'c_crop,w_1600,h_1066', height: 1066, width: 1600 },
    'landscape-crop6x': { transformation: 'c_crop,w_2400,h_1602', height: 1602, width: 2400 },
    'scaled-small': { transformation: 'w_320,h_320', height: 320, width: 320 },
    'scaled-medium': { transformation: 'w_750,h_750', height: 750, width: 750 },
    'scaled-large': { transformation: 'w_1490,h_1024,c_fill', width: 1490, height: 1024 },
    'scaled-xlarge': { transformation: 'w_2400,h_2400', height: 2400, width: 2400 },
    'square-small': { transformation: 'w_240,h_240', height: 240, width: 240 },
    'square-small2x': { transformation: 'w_480,h_480', height: 480, width: 480 },
    'facebook': { transformation: 'c_crop,w_1200,h_630', height: 630, width: 1200 },
    'twitter': { transformation: 'c_crop,w_600,h_314', height: 314, width: 600 },
    'banner-600': { transformation: 'w_600,h_200', height: 200, width: 600 },
    'banner-1200': { transformation: 'w_1200,h_400', height: 400, width: 1200 },
    'banner-1800': { transformation: 'w_1800,h_600', height: 600, width: 1800 },
    'hero-section-desktop': { transformation: 'w_1440,h_550', height: 550, width: 1440 },
    'hero-section-mobile': { transformation: 'w_390,h_625', height: 625, width: 390 },
    'default': { transformation: '' },
    // RAS quality tiers — used on the listing page
    'ras_hero':    { transformation: 'w_1400,c_limit', width: 1400 },
    'ras_product': { transformation: 'w_800,c_limit',  width: 800  },
    'ras_thumb':   { transformation: 'w_400,c_fill,g_auto', width: 400 },
  };

  const { format = 'auto', fetchFormat = 'auto', quality = 'auto:eco', dpr = 'auto', crop = 'fit' } = options;

  const splittedURL = secureUrl.split('/upload');

  if (splittedURL && splittedURL.length == 2 && variants[variant]) {
    return {
      variants,
      imageUrl: `${splittedURL[0]}/upload/${variants[variant].transformation},f_${format},q_${quality},dpr_${dpr}/${splittedURL[1]}`
    };
  }

  return { variants };
}

export function getCloudinarySecureUrl(publicId, options = {}) {
  const {
    folder = '',                    // Optional folder where the image is stored
    transformation = '',             // Optional transformations (e.g., 'w_400,h_400,c_fill')
    quality = 'auto:eco',            // Optional quality (default to aggressive compression)
    dpr = 'auto',                    // Optional device pixel ratio setting
  } = options;

  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD;

  // Construct the base URL with Cloudinary's standard secure path
  let secureUrl = `https://res.cloudinary.com/${cloudName}/image/upload/`;

  // Add transformation string (including format auto selection and other options)
  let transformationString = '';

  // Include additional transformations if provided
  if (transformation) {
    transformationString += `,${transformation}`;
  }

  // Append the transformations to the URL
  secureUrl += `${transformationString}/`;

  // Add folder path if provided
  if (folder) {
    secureUrl += `${folder}/`;
  }

  // Add public ID (without the need to specify a format)
  secureUrl += publicId;

  return secureUrl;
}


// Utility function to get a Cloudinary image URL with auto format negotiation.
// (Previously forced `f_webp`, which broke Safari < 14 — those browsers can't decode WebP
// and show a broken-image icon. `f_auto` lets Cloudinary serve WebP to browsers that accept
// it and JPEG/PNG to those that don't, based on the request's Accept header.)
export const getWebPImageUrl = (publicId, options = '') => {
  return `https://res.cloudinary.com/${cloudName}/image/upload/f_auto${options ? `,${options}` : ''}/${publicId}`;
};

// Normalizes an already-built Cloudinary delivery URL so the browser is served a format it
// can actually decode. Images in this app were historically uploaded/stored as WebP (see the
// `f_webp` upload transform), so many stored `secure_url`s end in `.webp` and are rendered
// raw. Safari < 14 (e.g. High Sierra/Safari 11.1, Mojave/Safari 12.1) can't decode WebP and
// shows a broken-image `?`. Inserting `f_auto` makes Cloudinary transcode on delivery to the
// best format the requesting browser accepts (and `f_auto` overrides the URL's file
// extension, so an existing `.webp` path is still served as JPEG to old Safari). No-op for
// non-Cloudinary URLs and URLs that already request auto format.
export const withCloudinaryAutoFormat = url => {
  if (!url || typeof url !== 'string') return url;
  if (url.indexOf('res.cloudinary.com') === -1) return url;
  const marker = '/image/upload/';
  const idx = url.indexOf(marker);
  if (idx === -1) return url;
  const head = url.slice(0, idx + marker.length);
  let tail = url.slice(idx + marker.length);
  // Already auto-format — leave it alone.
  if (/(^|[/,])f_auto([/,]|$)/.test(tail)) return url;
  // Strip a leading forced-format token (e.g. "f_webp," or "f_webp/") to avoid a format conflict.
  tail = tail.replace(/^f_[a-z0-9]+[,/]/, '');
  return `${head}f_auto/${tail}`;
};

// Like withCloudinaryAutoFormat, but forces JPEG (`f_jpg`). Use this wherever a Cloudinary image
// is rendered as a plain <img> WITHOUT a <picture> WebP-source fallback and must work on Safari
// < 14 (11.1/12.1/13.1). IMPORTANT: this Cloudinary account's `f_auto` serves WebP regardless of
// the request's Accept header (verified), and those Safari versions can't decode WebP — so f_auto
// leaves them with a broken image. `f_jpg` guarantees a decodable format everywhere. No-op for
// non-Cloudinary URLs and URLs that already request `f_jpg`.
export const withCloudinaryJpgFormat = url => {
  if (!url || typeof url !== 'string') return url;
  if (url.indexOf('res.cloudinary.com') === -1) return url;
  const marker = '/image/upload/';
  const idx = url.indexOf(marker);
  if (idx === -1) return url;
  const head = url.slice(0, idx + marker.length);
  let tail = url.slice(idx + marker.length);
  if (/(^|[/,])f_jpg([/,]|$)/.test(tail)) return url;
  // Strip a leading forced-format token (e.g. "f_auto," or "f_webp/") to avoid a format conflict.
  tail = tail.replace(/^f_[a-z0-9]+[,/]/, '');
  return `${head}f_jpg/${tail}`;
};

// Inserts a resize transformation into a Cloudinary URL.
// Uses c_limit so the image is only downscaled — never upscaled — and aspect ratio is preserved.
// Returns the URL unchanged if it is not a Cloudinary URL.
export const resizeCloudinaryUrl = (url, width = 1024, height = 1024) => {
  if (!url || !url.includes('res.cloudinary.com')) return url;
  return url.replace('/image/upload/', `/image/upload/w_${width},h_${height},c_limit/`);
};


/**
 * Upload a video file to Cloudinary (single request - for small files)
 * @param {Object} params - { file: File, id: string, transformation?: string }
 * @returns {Promise<Object>} Cloudinary upload response
 */
export const UploadVideo = async ({ file, id, transformation = '', folder = '' }) => {
  try {
    // Get the signature and timestamp from your server
    const signatureResponse = await getCloudinarySignature({ public_id: id, transformation, folder });
    const { signature, timestamp } = signatureResponse;

    // Prepare the FormData for the video upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('public_id', id); // optional, can let Cloudinary generate it
    formData.append('api_key', apiKey);
    formData.append('timestamp', timestamp);
    formData.append('signature', signature);
    if (transformation) {
      formData.append('transformation', transformation);
    }
    if (folder) {
      formData.append('folder', folder);
    }
    // For video, set resource_type
    formData.append('resource_type', 'video');

    // Upload to Cloudinary (video endpoint)
    const response = await axios.post(
      uploadEndpoint,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    return response.data;
  } catch (error) {
    console.error('Error uploading video:', error);
    throw error;
  }
};

// ================ Chunked Video Upload ================ //

// Chunk upload constants (can be imported from constants.js if needed)
const VIDEO_CHUNK_SIZE = 6 * 1024 * 1024; // 6MB per chunk
const MAX_CHUNK_RETRIES = 3;
const CHUNK_RETRY_BASE_DELAY = 1000; // 1 second
const CHUNK_UPLOAD_TIMEOUT = 120000; // 2 minutes
const SMALL_VIDEO_THRESHOLD = 20 * 1024 * 1024; // 20MB

/**
 * Helper to sleep for a given duration
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Check if an error is retryable (network errors, server errors, timeouts)
 */
const isRetryableError = (error) => {
  // Network errors (no response)
  if (!error.response) return true;

  const status = error.response?.status;
  // 5xx server errors, 408 timeout, 429 rate limit
  return status >= 500 || status === 408 || status === 429;
};

/**
 * Upload a single chunk to Cloudinary
 */
const uploadSingleChunk = async ({
  chunk,
  start,
  end,
  fileSize,
  uniqueUploadId,
  publicId,
  folder,
  signature,
  timestamp,
}) => {
  const formData = new FormData();
  formData.append('file', chunk);
  formData.append('public_id', publicId);
  formData.append('api_key', apiKey);
  formData.append('timestamp', timestamp);
  formData.append('signature', signature);
  formData.append('resource_type', 'video');
  if (folder) {
    formData.append('folder', folder);
  }

  const response = await axios.post(uploadEndpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'X-Unique-Upload-Id': uniqueUploadId,
      'Content-Range': `bytes ${start}-${end - 1}/${fileSize}`,
    },
    timeout: CHUNK_UPLOAD_TIMEOUT,
  });

  return response.data;
};

/**
 * Upload a chunk with automatic retry on failure
 */
const uploadChunkWithRetry = async (params, retryCount = 0) => {
  try {
    return await uploadSingleChunk(params);
  } catch (error) {
    const canRetry = isRetryableError(error);
    const hasRetriesLeft = retryCount < MAX_CHUNK_RETRIES;

    if (canRetry && hasRetriesLeft) {
      const delay = CHUNK_RETRY_BASE_DELAY * Math.pow(2, retryCount);
      console.warn(
        `Chunk upload failed (bytes ${params.start}-${params.end - 1}), ` +
        `retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_CHUNK_RETRIES})`,
        error.message
      );

      await sleep(delay);
      return uploadChunkWithRetry(params, retryCount + 1);
    }

    // Non-retryable error or exhausted retries
    const errorMsg = error.response?.data?.error?.message || error.message || 'Unknown error';
    throw new Error(
      `Chunk upload failed after ${retryCount} retries: ${errorMsg}`
    );
  }
};

/**
 * Upload video in chunks with retry logic for large files.
 * Automatically falls back to single upload for small files.
 * 
 * @param {Object} params
 * @param {File} params.file - Video file to upload
 * @param {string} params.id - Public ID for Cloudinary
 * @param {string} params.folder - Folder path in Cloudinary
 * @param {string} params.transformation - Optional transformation string
 * @param {Function} params.onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} Cloudinary upload response
 */
export const UploadVideoChunked = async ({
  file,
  id,
  folder = '',
  transformation = '',
  onProgress
}) => {
  const fileSize = file.size;

  // Use single upload for small files
  if (fileSize <= SMALL_VIDEO_THRESHOLD) {
    console.log(`Video ${file.name} (${(fileSize / 1024 / 1024).toFixed(1)}MB) using single upload`);
    onProgress?.(0);
    const result = await UploadVideo({ file, id, transformation, folder });
    onProgress?.(100);
    return result;
  }

  console.log(
    `Video ${file.name} (${(fileSize / 1024 / 1024).toFixed(1)}MB) using chunked upload ` +
    `(${Math.ceil(fileSize / VIDEO_CHUNK_SIZE)} chunks)`
  );

  const totalChunks = Math.ceil(fileSize / VIDEO_CHUNK_SIZE);
  const uniqueUploadId = `${id}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  // Get signature for the upload session
  const { signature, timestamp } = await getCloudinarySignature({
    public_id: id,
    transformation,
    folder,
  });

  let uploadedBytes = 0;
  let lastResult = null;

  onProgress?.(0);

  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    const start = chunkIndex * VIDEO_CHUNK_SIZE;
    const end = Math.min(start + VIDEO_CHUNK_SIZE, fileSize);
    const chunk = file.slice(start, end);

    console.log(
      `Uploading chunk ${chunkIndex + 1}/${totalChunks} ` +
      `(bytes ${start}-${end - 1}, ${((end - start) / 1024 / 1024).toFixed(1)}MB)`
    );

    // Upload chunk with retry
    lastResult = await uploadChunkWithRetry({
      chunk,
      start,
      end,
      fileSize,
      uniqueUploadId,
      publicId: id,
      folder,
      signature,
      timestamp,
    });

    uploadedBytes = end;
    const progress = Math.round((uploadedBytes / fileSize) * 100);
    onProgress?.(progress);

    console.log(`Chunk ${chunkIndex + 1}/${totalChunks} uploaded (${progress}% complete)`);
  }

  // The last chunk response contains the final upload result
  console.log('Chunked upload complete:', lastResult?.public_id);
  return lastResult;
};

/**
 * Delete media from Cloudinary
 * @param {Object} params - { publicId: string, resourceType?: 'image' | 'video' }
 * @returns {Promise<Object>} Delete response
 */
export const DeleteMedia = async ({ publicId, resourceType = 'image' }) => {
  try {
    const { deleteImage } = await import('./api');
    return await deleteImage({ publicId, resourceType });
  } catch (error) {
    console.error('Error deleting media:', error);
    throw error;
  }
};

export default cloudinaryInstance;
/**
 * Build the exact `srcSet` / `sizes` / default-URL trio that <ResponsiveImage>'s Cloudinary
 * branch renders for an image.
 *
 * This lives here rather than inline in ResponsiveImage so that a `<link rel="preload">` can be
 * generated from the same inputs and is guaranteed to request the identical URL. The two were
 * previously computed separately on the listing page and had drifted: the preload asked for
 * `ras_hero` (w_1400, q_auto:good) while the gallery rendered `ras_product` (w_800, q_auto:eco),
 * so the high-priority preload spent a full-size download on a file the page never displayed and
 * the real LCP image only began downloading once React had hydrated.
 *
 * `srcset` is intentionally keyed off a successfully built URL while `sizes` is keyed off the
 * variant merely existing - that asymmetry matches the behaviour this replaced, so a malformed
 * secure_url (one with no `/upload` segment) degrades exactly as it did before.
 */
export function buildCloudinaryImageSources({
  secureUrl,
  variantsToRender = ['listing-card'],
  defaultVariant = 'listing-card',
  options = cloudinaryModernOptions,
  format,
}) {
  const resolvedOptions = format ? { ...options, format } : options;
  const { variants } = getUrlVariants(secureUrl, defaultVariant, resolvedOptions);

  const srcset = variantsToRender
    .map(variantKey => {
      if (!variants[variantKey]) return null;
      const { imageUrl } = getUrlVariants(secureUrl, variantKey, resolvedOptions);
      return imageUrl ? `${imageUrl} ${variants[variantKey].width}w` : null;
    })
    .filter(Boolean)
    .join(', ');

  const sizes = variantsToRender
    .map(variantKey =>
      variants[variantKey]
        ? `(max-width: ${variants[variantKey].width}px) ${variants[variantKey].width}px`
        : null
    )
    .filter(Boolean)
    .join(', ');

  return {
    srcset,
    sizes,
    defaultUrl: getUrlVariants(secureUrl, defaultVariant, resolvedOptions).imageUrl,
  };
}
